/**
 * Admin-invite-app-user modal (Topic 4 PR-2).
 * Spec: docs/superpowers/specs/2026-05-28-admin-create-user-design.md §9.
 *
 * Permission-gated by `users.create` at the caller (the Users list page
 * hides the trigger button if the admin doesn't hold it); this component
 * does NOT re-check permission — backend is the authoritative gate and
 * returns 403 if a user reaches this modal without `users.create` (e.g.
 * the permission was revoked between page load and submit).
 *
 * Posts to POST /v1/admin/app-users via `adminFetch` (the single
 * chokepoint — never raw fetch). On success the caller can navigate to
 * the new user's detail page via the user_id returned in onClose.
 */
import { useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { X, UserPlus, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Pre-grant segments allow-list. Mirrors `ALLOWED_SEGMENTS` in
 * backend/src/services/admin-create-user.ts — keep them in sync. The
 * backend re-validates so a drifted FE list is a UX bug (rejected
 * submit) not a security bug.
 */
const ALLOWED_SEGMENTS = ['friends_family', 'founding_100', 'beta_general', 'influencer'];

const SEGMENT_LABELS: Record<string, string> = {
  friends_family: 'Friends & Family',
  founding_100: 'Founding 100',
  beta_general: 'Beta (general)',
  influencer: 'Influencer',
};

export interface InviteAppUserModalProps {
  open: boolean;
  onClose: (created?: { user_id: string; email: string }) => void;
}

interface InviteResult {
  user_id: string;
  email: string;
  display_name: string;
  segments: string[];
  cognito_user_sub: string;
  /** When the invite actually left; null when it was withheld or failed. */
  invite_email_sent_at: string | null;
  status: 'invited';
}

export function InviteAppUserModal({ open, onClose }: InviteAppUserModalProps) {
  const apiUrl = useApiUrl();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [segments, setSegments] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read environment from Vite at compile time; falls back to 'dev' so a
  // local dev build that didn't set VITE_ENV won't show a PROD banner.
  const isProd = (import.meta as unknown as { env?: { VITE_ENV?: string } }).env?.VITE_ENV === 'prod';

  if (!open) return null;

  const toggleSegment = (s: string) => {
    setSegments((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const resetForm = () => {
    setEmail('');
    setDisplayName('');
    setSegments(new Set());
    setError(null);
  };

  const handleClose = (created?: { user_id: string; email: string }) => {
    resetForm();
    onClose(created);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const resp = await adminFetch(`${apiUrl}/v1/admin/app-users`, {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(),
          display_name: displayName.trim(),
          segments: segments.size > 0 ? [...segments] : undefined,
        }),
      });
      const data: InviteResult = await resp.json();
      handleClose({ user_id: data.user_id, email: data.email });
    } catch (err) {
      // Map known status codes to actionable messages. Anything else
      // falls through to the raw API message — the chokepoint already
      // unwraps the body's `message` field when present.
      if (err instanceof AdminApiError) {
        if (err.status === 409) {
          setError('A user with that email already exists.');
        } else if (err.status === 429) {
          setError('Rate limit reached — max 10 invites per minute. Wait a moment and try again.');
        } else if (err.status === 403) {
          setError('You do not have permission to invite users (users.create).');
        } else if (err.status === 400) {
          setError(err.message || 'Invalid request.');
        } else {
          setError(err.message || `Request failed (${err.status})`);
        }
      } else {
        setError(err instanceof Error ? err.message : 'Failed to invite user');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled = submitting || !email.trim() || !displayName.trim();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-app-user-title"
    >
      <div className="w-full max-w-lg rounded-lg bg-card p-6 shadow-lg">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <UserPlus className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 id="invite-app-user-title" className="text-lg font-semibold">
                Invite app user
              </h3>
              <p className="text-sm text-muted-foreground">
                Send a Cognito welcome email with a temporary password.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleClose()}
            disabled={submitting}
            aria-label="Close"
            className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Environment banner — load-bearing because PROD invitees get real emails. */}
        <div
          className={
            isProd
              ? 'mb-4 flex items-start gap-2 rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive'
              : 'mb-4 flex items-start gap-2 rounded-lg border border-yellow-500 bg-yellow-50 p-3 text-sm text-yellow-800'
          }
        >
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            {isProd ? (
              <>
                <strong>Environment: PRODUCTION.</strong> This invitee receives a
                real Cognito welcome email and can sign in immediately.
                Double-check the email address.
              </>
            ) : (
              <>
                <strong>Environment: DEV.</strong> Invitee receives a real email
                but signs into a DEV-only build.
              </>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="invite-email" className="text-sm font-medium">
              Email <span className="text-destructive">*</span>
            </label>
            <input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              autoComplete="off"
              placeholder="invitee@example.com"
              className="mt-1 h-10 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
          </div>

          {/* Display name */}
          <div>
            <label htmlFor="invite-display-name" className="text-sm font-medium">
              Display name <span className="text-destructive">*</span>
            </label>
            <input
              id="invite-display-name"
              type="text"
              required
              maxLength={100}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={submitting}
              placeholder="Jane Doe"
              className="mt-1 h-10 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Shown to the invitee on signup. Max 100 chars.
            </p>
          </div>

          {/* Segments */}
          <fieldset disabled={submitting} className="rounded-lg border bg-muted/30 p-3">
            <legend className="px-1 text-sm font-medium">
              Pre-grant segments (optional)
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {ALLOWED_SEGMENTS.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={segments.has(s)}
                    onChange={() => toggleSegment(s)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  {SEGMENT_LABELS[s] ?? s}
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Segments unlock ELIGIBILITY_OPEN downstream. Pick zero to invite a
              regular user.
            </p>
          </fieldset>

          {/* Invitee experience */}
          <details className="rounded-lg border bg-muted/20 p-3 text-sm">
            <summary className="cursor-pointer font-medium">Invitee experience</summary>
            <ol className="mt-2 list-decimal pl-5 text-xs text-muted-foreground space-y-1">
              <li>Cognito sends a welcome email with a temporary password.</li>
              <li>Invitee opens the mobile app + signs in with email + temp password.</li>
              <li>Sets a permanent password (FORCE_CHANGE_PASSWORD flow).</li>
              <li>Goes through signup wizard + Abby onboarding normally.</li>
              <li>Selected segments grant downstream ELIGIBILITY_OPEN when applicable.</li>
            </ol>
          </details>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={() => handleClose()}
              disabled={submitting}
              className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitDisabled}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4" />
                  Send invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
